/**
 * DesignChangeCoordinator — Phase 9 (spec §17A).
 *
 * Turns the pure diff engine's ChangeFindings into scenario scope through
 * ONE reconciliation boundary:
 *
 *   plan commit → diff(baseline, proposed) → findings
 *     → auto-apply unambiguous added/removed (unless the user IGNORED them)
 *     → apply ACCEPTED review findings (moved wall = Remove old + Build new)
 *     → bind each finding root to its Feature via change_scope_bindings
 *       (FK-backed; several roots aggregate into one Feature per design)
 *     → feature quantity = Σ bound root quantities → assemblies regenerate
 *
 * Rules preserved:
 *  - NEVER duplicates Phase 8 auto-scope: Proposed-only objects are
 *    informational here; their Install scope lives on the object.
 *  - Replace never double-counts demolition: an object root that still
 *    exists in the Proposed design (kept or replaced) yields no removal.
 *  - Keep creates no Features. Boundaries create nothing.
 *  - Undo reconciles: findings disappear → bindings drop → auto-created,
 *    untouched, financially-unreferenced features archive; anything touched
 *    or actual-linked survives with its quantity cleared; roots returning
 *    resurrect the archived feature (Phase 8 rule generalized).
 *  - Existing accounting is never touched.
 */
import { diffPlans, type ChangeFinding } from "../geometry";
import { type FeatureGeometryBinding } from "../domain/feature";
import type { DbExecutor } from "./executor";
import type { PlanRepository } from "./repositories/planRepository";
import type { FeatureRepository } from "./repositories/featureRepository";
import type { AssemblyRepository } from "./repositories/assemblyRepository";
import type { DesignScopeCoordinator } from "./designScopeCoordinator";

export type ChangeDisposition = "ignored" | "accepted";

export interface ReviewedChangeFinding extends ChangeFinding {
  disposition: ChangeDisposition | null;
  /** True when this finding currently contributes scope. */
  applied: boolean;
  featureId: string | null;
}

interface ScopeContribution {
  entityKind: "wall" | "opening" | "object";
  rootId: string;
  featureDefinitionId: string;
  action: "install" | "remove";
  bindingKind: FeatureGeometryBinding;
  quantityX100: number;
  findingKey: string;
}

interface BindingRow {
  design_id: string;
  entity_kind: string;
  root_wall_id: string | null;
  root_opening_id: string | null;
  root_object_id: string | null;
  feature_id: string;
  quantity_x100: number | null;
}

export class DesignChangeCoordinator {
  constructor(
    private readonly db: DbExecutor,
    private readonly plans: PlanRepository,
    private readonly features: FeatureRepository,
    private readonly assemblies: AssemblyRepository,
    private readonly scope: DesignScopeCoordinator,
  ) {}

  private async designInfo(designId: string): Promise<{ projectId: string; kind: string; createdAt: string; archived: boolean } | null> {
    const rows = await this.db.select<{ project_id: string; kind: string; created_at: string; archived_at: string | null }>(
      "SELECT project_id, kind, created_at, archived_at FROM designs WHERE id = ?",
      [designId],
    );
    if (rows.length === 0) return null;
    return { projectId: rows[0].project_id, kind: rows[0].kind, createdAt: rows[0].created_at, archived: rows[0].archived_at !== null };
  }

  /** Compute the raw findings for one proposed design (all floors). */
  async computeFindings(designId: string): Promise<ChangeFinding[]> {
    const info = await this.designInfo(designId);
    if (!info || info.kind !== "proposed") return [];
    const project = await this.db.select<{ project_type: string }>("SELECT project_type FROM projects WHERE id = ?", [info.projectId]);
    const isRenovation = (project[0]?.project_type ?? "renovation") === "renovation";
    const existing = isRenovation
      ? await this.db.select<{ id: string }>("SELECT id FROM designs WHERE project_id = ? AND kind = 'existing' AND archived_at IS NULL", [info.projectId])
      : [];
    const baselineId = existing[0]?.id ?? null;
    // Existing corrections: baseline entities documented AFTER this design
    // was created never auto-generate demolition (spec §17A).
    const corrected = new Set<string>();
    if (baselineId) {
      for (const table of ["walls", "wall_openings", "plan_objects"]) {
        const scopeClause = table === "wall_openings" ? "wall_id IN (SELECT id FROM walls WHERE design_id = ?)" : "design_id = ?";
        const rows = await this.db.select<{ id: string }>(
          `SELECT id FROM ${table} WHERE ${scopeClause} AND created_at > ?`,
          [baselineId, info.createdAt],
        );
        for (const r of rows) corrected.add(r.id);
      }
    }
    const floors = await this.db.select<{ id: string }>(
      "SELECT id FROM floors WHERE project_id = ? AND archived_at IS NULL",
      [info.projectId],
    );
    const findings: ChangeFinding[] = [];
    for (const floor of floors) {
      const proposed = await this.plans.loadFloor(designId, floor.id);
      const baseline = baselineId ? await this.plans.loadFloor(baselineId, floor.id) : null;
      findings.push(...diffPlans(baseline, proposed, { baselineCorrectedIds: corrected }));
    }
    return findings;
  }

  /** Findings + dispositions + current bindings, for the Change Review UI. */
  async reviewFindings(designId: string): Promise<ReviewedChangeFinding[]> {
    const findings = await this.computeFindings(designId);
    const dispositions = new Map(
      (
        await this.db.select<{ finding_key: string; disposition: string }>(
          "SELECT finding_key, disposition FROM design_change_dispositions WHERE design_id = ?",
          [designId],
        )
      ).map((r) => [r.finding_key, r.disposition as ChangeDisposition]),
    );
    const bindings = await this.listBindings(designId);
    const featureByRoot = new Map<string, string>();
    for (const b of bindings) {
      featureByRoot.set(`${b.entity_kind}:${b.root_wall_id ?? b.root_opening_id ?? b.root_object_id}`, b.feature_id);
    }
    // Phase 8 object scope: an informational added-object finding is
    // "applied" when the object itself is feature-bound.
    const objectFeatures = new Map(
      (
        await this.db.select<{ id: string; feature_id: string | null }>(
          "SELECT id, feature_id FROM plan_objects WHERE design_id = ? AND archived_at IS NULL",
          [designId],
        )
      ).map((r) => [r.id, r.feature_id]),
    );
    return findings.map((f) => {
      const disposition = dispositions.get(f.key) ?? null;
      const boundFeature =
        f.informational && f.entityKind === "object"
          ? (objectFeatures.get(f.rootId) ?? null)
          : (featureByRoot.get(`${f.entityKind}:${f.rootId}`) ?? featureByRoot.get(`${f.entityKind}:${f.proposedIds[0] ?? ""}`) ?? null);
      return { ...f, disposition, applied: boundFeature !== null, featureId: boundFeature };
    });
  }

  async setDisposition(designId: string, findingKey: string, disposition: ChangeDisposition | null): Promise<void> {
    await this.db.transaction(async () => {
      await this.db.execute("DELETE FROM design_change_dispositions WHERE design_id = ? AND finding_key = ?", [designId, findingKey]);
      if (disposition) {
        await this.db.execute(
          "INSERT INTO design_change_dispositions (design_id, finding_key, disposition, created_at) VALUES (?, ?, ?, ?)",
          [designId, findingKey, disposition, new Date().toISOString()],
        );
      }
      await this.reconcileDesign(designId);
    });
  }

  /**
   * Entry point from plan commits: an edit to the Existing design changes
   * EVERY proposed design's diff; an edit to a proposed design changes its
   * own.
   */
  async reconcileAfterCommit(designId: string): Promise<string[]> {
    const info = await this.designInfo(designId);
    if (!info) return [];
    if (info.kind === "existing") {
      const proposed = await this.db.select<{ id: string }>(
        "SELECT id FROM designs WHERE project_id = ? AND kind = 'proposed' AND archived_at IS NULL",
        [info.projectId],
      );
      const touched: string[] = [];
      for (const d of proposed) touched.push(...(await this.reconcileDesign(d.id)));
      return touched;
    }
    return this.reconcileDesign(designId);
  }

  private async listBindings(designId: string): Promise<BindingRow[]> {
    return this.db.select<BindingRow>("SELECT * FROM change_scope_bindings WHERE design_id = ?", [designId]);
  }

  /** Geometry roots bound to a change-scope feature (estimate-row → plan
   *  navigation). Removed-entity roots live in the BASELINE (Existing)
   *  design; `rootDesignId`/`floorId` say where to look. */
  async rootsForFeature(featureId: string): Promise<{ designId: string | null; rootDesignId: string | null; floorId: string | null; wallIds: string[]; openingIds: string[]; objectIds: string[] }> {
    const rows = await this.db.select<BindingRow>("SELECT * FROM change_scope_bindings WHERE feature_id = ?", [featureId]);
    const wallIds = rows.map((r) => r.root_wall_id).filter((x): x is string => x !== null);
    const openingIds = rows.map((r) => r.root_opening_id).filter((x): x is string => x !== null);
    const objectIds = rows.map((r) => r.root_object_id).filter((x): x is string => x !== null);
    let rootDesignId: string | null = null;
    let floorId: string | null = null;
    if (wallIds.length > 0) {
      const w = await this.db.select<{ design_id: string; floor_id: string }>("SELECT design_id, floor_id FROM walls WHERE id = ?", [wallIds[0]]);
      rootDesignId = w[0]?.design_id ?? null;
      floorId = w[0]?.floor_id ?? null;
    } else if (objectIds.length > 0) {
      const o = await this.db.select<{ design_id: string; floor_id: string }>("SELECT design_id, floor_id FROM plan_objects WHERE id = ?", [objectIds[0]]);
      rootDesignId = o[0]?.design_id ?? null;
      floorId = o[0]?.floor_id ?? null;
    } else if (openingIds.length > 0) {
      const o = await this.db.select<{ design_id: string; floor_id: string }>(
        "SELECT w.design_id, w.floor_id FROM wall_openings op JOIN walls w ON w.id = op.wall_id WHERE op.id = ?",
        [openingIds[0]],
      );
      rootDesignId = o[0]?.design_id ?? null;
      floorId = o[0]?.floor_id ?? null;
    }
    return { designId: rows[0]?.design_id ?? null, rootDesignId, floorId, wallIds, openingIds, objectIds };
  }

  /** The scope contributions a finding implies given its disposition. */
  private contributions(f: ChangeFinding, disposition: ChangeDisposition | null): ScopeContribution[] {
    if (f.informational) return [];
    if (disposition === "ignored") return [];
    const out: ScopeContribution[] = [];
    const push = (rootId: string, fd: string, action: "install" | "remove", unit: "lf" | "each", qty: number) =>
      out.push({
        entityKind: f.entityKind,
        rootId,
        featureDefinitionId: fd,
        action,
        bindingKind: unit === "lf" ? "change-wall-length" : "change-count",
        quantityX100: qty,
        findingKey: f.key,
      });
    if (f.autoApply && f.scope) {
      push(f.rootId, f.scope.featureDefinitionId, f.scope.action, f.scope.unit, f.quantityX100 ?? 100);
      return out;
    }
    if (disposition !== "accepted") return [];
    // Accepted review findings with a deterministic construction meaning:
    if (f.key.startsWith("wall-moved:")) {
      push(f.rootId, "remove-interior-wall", "remove", "lf", f.quantityX100 ?? 100);
      // build side: each proposed segment contributes its own root
      // (quantity recorded on the finding is the BASELINE length; the build
      // side re-measures per segment at apply time via quantityX100 split —
      // conservative: bind the total to the first segment's root).
      if (f.proposedIds.length > 0) push(f.proposedIds[0], "build-interior-wall", "install", "lf", f.quantityX100 ?? 100);
      return out;
    }
    if (f.key.startsWith("wall-partial:") || f.key.startsWith("wall-corrected:")) {
      push(f.rootId, "remove-interior-wall", "remove", "lf", f.quantityX100 ?? 100);
      return out;
    }
    if (f.key.startsWith("opening-moved:")) {
      const fd = f.explanation.includes("window") ? "remove-window-opening" : "remove-door-opening";
      const fi = f.explanation.includes("window") ? "install-window" : "install-interior-door";
      // §79.1: when the old host wall is itself demolished there is no old
      // opening to close — accepting the move contributes the install only.
      if (!f.supersededRemoval) push(f.rootId, fd, "remove", "each", 100);
      if (f.proposedIds.length > 0) push(f.proposedIds[0], fi, "install", "each", 100);
      return out;
    }
    if (f.key.startsWith("opening-corrected:")) {
      const fd = f.explanation.includes("window") ? "remove-window-opening" : "remove-door-opening";
      push(f.rootId, fd, "remove", "each", 100);
      return out;
    }
    if (f.key.startsWith("object-corrected:")) {
      push(f.rootId, "remove-fixture", "remove", "each", 100);
      return out;
    }
    // wall-modified / opening-resized / object-moved: accepting marks them
    // reviewed; any cost is added manually (no fake precision).
    return out;
  }

  /** Recompute findings and reconcile scope bindings/features for one design. */
  async reconcileDesign(designId: string): Promise<string[]> {
    const info = await this.designInfo(designId);
    if (!info || info.kind !== "proposed" || info.archived) return [];
    return this.db.transaction(async () => {
      const findings = await this.computeFindings(designId);
      const dispositions = new Map(
        (
          await this.db.select<{ finding_key: string; disposition: string }>(
            "SELECT finding_key, disposition FROM design_change_dispositions WHERE design_id = ?",
            [designId],
          )
        ).map((r) => [r.finding_key, r.disposition as ChangeDisposition]),
      );
      const desired = new Map<string, ScopeContribution>();
      for (const f of findings) {
        for (const c of this.contributions(f, dispositions.get(f.key) ?? null)) {
          desired.set(`${c.entityKind}:${c.rootId}:${c.action}`, c);
        }
      }
      const existing = await this.listBindings(designId);
      const replacementPairs = new Map(findings.filter(f => f.entityKind === "object" && f.supersededRemoval && f.proposedIds[0] && f.baselineIds[0]).map(f => [f.proposedIds[0], f.baselineIds[0]]));
      const replacementIds = await this.scope.reconcileAutomaticReplacementRemoval(designId, replacementPairs);
      const touched = new Set<string>();
      const now = new Date().toISOString();
      const rootColumn = (kind: string): "root_wall_id" | "root_opening_id" | "root_object_id" =>
        kind === "wall" ? "root_wall_id" : kind === "opening" ? "root_opening_id" : "root_object_id";
      // Existing binding index: entityKind:rootId → row. (One root may carry
      // remove AND build via different roots, so the key needs no action.)
      const existingByRoot = new Map<string, BindingRow>();
      for (const b of existing) existingByRoot.set(`${b.entity_kind}:${b.root_wall_id ?? b.root_opening_id ?? b.root_object_id}`, b);
      const desiredByRoot = new Map<string, ScopeContribution>();
      for (const c of desired.values()) desiredByRoot.set(`${c.entityKind}:${c.rootId}`, c);

      // Drop bindings whose finding disappeared (undo / re-draw / ignore).
      for (const b of existing) {
        const key = `${b.entity_kind}:${b.root_wall_id ?? b.root_opening_id ?? b.root_object_id}`;
        if (!desiredByRoot.has(key)) {
          await this.db.execute(
            `DELETE FROM change_scope_bindings WHERE design_id = ? AND ${rootColumn(b.entity_kind)} = ?`,
            [designId, b.root_wall_id ?? b.root_opening_id ?? b.root_object_id],
          );
          touched.add(b.feature_id);
        }
      }
      // Add/update bindings for desired contributions.
      for (const c of desiredByRoot.values()) {
        const key = `${c.entityKind}:${c.rootId}`;
        const current = existingByRoot.get(key);
        if (current) {
          if (Number(current.quantity_x100 ?? -1) !== c.quantityX100) {
            await this.db.execute(
              `UPDATE change_scope_bindings SET quantity_x100 = ? WHERE design_id = ? AND ${rootColumn(c.entityKind)} = ?`,
              [c.quantityX100, designId, c.rootId],
            );
            touched.add(current.feature_id);
          }
          continue;
        }
        const featureId = await this.findOrCreateFeature(designId, info.projectId, c, now);
        await this.db.execute(
          `INSERT INTO change_scope_bindings (design_id, entity_kind, ${rootColumn(c.entityKind)}, feature_id, quantity_x100, created_at)
           VALUES (?, ?, ?, ?, ?, ?)`,
          [designId, c.entityKind, c.rootId, featureId, c.quantityX100, now],
        );
        touched.add(featureId);
      }
      // Recompute quantities for touched features (archive/restore per
      // rules), THEN auto-attach default assemblies — the quantity must
      // exist before a per-LF assembly can attach.
      for (const featureId of touched) {
        await this.reconcileFeatureQuantity(featureId, now);
      }
      for (const featureId of touched) {
        await this.autoAttachIfNeeded(featureId);
      }
      return [...touched, ...replacementIds];
    });
  }

  private async findOrCreateFeature(designId: string, projectId: string, c: ScopeContribution, now: string): Promise<string> {
    // Aggregate per (design, feature definition): all removed walls share one
    // "Remove Interior Walls" feature, etc.
    const active = await this.db.select<{ id: string }>(
      `SELECT id FROM project_features WHERE design_id = ? AND feature_definition_id = ?
        AND quantity_geometry_binding IN ('change-wall-length','change-count') AND archived_at IS NULL LIMIT 1`,
      [designId, c.featureDefinitionId],
    );
    if (active.length > 0) return active[0].id;
    // Resurrect an archived auto feature rather than duplicating it.
    const archived = await this.db.select<{ id: string }>(
      `SELECT id FROM project_features WHERE design_id = ? AND feature_definition_id = ?
        AND quantity_geometry_binding IN ('change-wall-length','change-count') AND archived_at IS NOT NULL LIMIT 1`,
      [designId, c.featureDefinitionId],
    );
    if (archived.length > 0) {
      await this.db.execute("UPDATE project_features SET archived_at = NULL, updated_at = ? WHERE id = ?", [now, archived[0].id]);
      return archived[0].id;
    }
    const fd = await this.db.select<{ display_name: string }>("SELECT display_name FROM feature_definitions WHERE id = ?", [c.featureDefinitionId]);
    const feature = await this.features.create(projectId, {
      designId,
      roomId: null,
      floorId: null,
      featureDefinitionId: c.featureDefinitionId,
      name: fd[0]?.display_name ?? c.featureDefinitionId,
      action: c.action,
      quantityX100: null,
      quantityUnit: null,
      notes: "",
    });
    await this.features.setGeometryBinding(feature.id, c.bindingKind, null);
    return feature.id;
  }

  /** Phase 8.1 rule, Phase 9 flavor: attach the single unit-compatible
   *  suggested template AFTER the aggregated quantity exists (a per-LF
   *  assembly cannot attach to a quantity-less feature). */
  private async autoAttachIfNeeded(featureId: string): Promise<void> {
    try { await this.scope.ensureDefaultAssembly(featureId); }
    catch (err) { console.error("Change-scope auto-attach failed:", err); }
  }

  private async reconcileFeatureQuantity(featureId: string, now: string): Promise<void> {
    const rows = await this.db.select<{ total: number | null; n: number }>(
      "SELECT SUM(quantity_x100) AS total, COUNT(*) AS n FROM change_scope_bindings WHERE feature_id = ?",
      [featureId],
    );
    const count = Number(rows[0]?.n ?? 0);
    const total = rows[0]?.total === null || rows[0]?.total === undefined ? null : Number(rows[0].total);
    if (count === 0) {
      if (await this.scope.isAutoRemovable(featureId)) {
        await this.db.execute("UPDATE project_features SET archived_at = ?, updated_at = ?, quantity_geometry_x100 = NULL WHERE id = ? AND archived_at IS NULL", [now, now, featureId]);
        await this.scope.archiveGeneratedLines(featureId, now);
        return;
      }
      await this.features.setGeometryQuantity(featureId, null);
      try {
        await this.assemblies.regenerateForFeature(featureId);
      } catch {
        /* surfaced in the assembly panel */
      }
      return;
    }
    await this.db.execute("UPDATE project_features SET archived_at = NULL WHERE id = ? AND archived_at IS NOT NULL", [featureId]);
    await this.features.setGeometryQuantity(featureId, total);
    try {
      await this.assemblies.regenerateForFeature(featureId);
    } catch {
      /* surfaced in the assembly panel */
    }
  }
}
